/**
 * Debug utility for controlled logging
 * Only logs in development environment unless explicitly forced
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface DebugOptions {
  force?: boolean; // Force logging even in production
  collapsed?: boolean; // Use console.groupCollapsed for grouped logs
  module?: string; // Module/component name for better tracking
}

class DebugHelper {
  private isDev = process.env.NODE_ENV !== 'production';
  private enabledModules: Set<string> = new Set();

  /**
   * Enable debug logging for specific modules
   */
  enableModule(module: string) {
    this.enabledModules.add(module);
  }

  /**
   * Disable debug logging for specific modules
   */
  disableModule(module: string) {
    this.enabledModules.delete(module);
  }

  /**
   * Check if logging should occur
   */
  private shouldLog(options?: DebugOptions): boolean {
    if (options?.force) return true;
    if (!this.isDev) return false;
    if (options?.module && !this.enabledModules.has(options.module)) {
      return false;
    }
    return true;
  }

  /**
   * Format the prefix for log messages
   */
  private getPrefix(level: LogLevel, module?: string): string {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
    const modulePrefix = module ? `[${module}]` : '';
    const levelEmoji = {
      log: '📝',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      debug: '🔍'
    };
    return `${levelEmoji[level]} ${timestamp} ${modulePrefix}`;
  }

  /**
   * Generic log method
   */
  private logWithLevel(level: LogLevel, args: any[], options?: DebugOptions) {
    if (!this.shouldLog(options)) return;

    const prefix = this.getPrefix(level, options?.module);
    const consoleMethod = console[level] || console.log;

    if (args.length === 1 && typeof args[0] === 'string') {
      consoleMethod(`${prefix} ${args[0]}`);
    } else {
      consoleMethod(prefix, ...args);
    }
  }

  /**
   * Standard log
   */
  log(...args: any[]) {
    this.logWithLevel('log', args);
  }

  /**
   * Info log
   */
  info(...args: any[]) {
    this.logWithLevel('info', args);
  }

  /**
   * Warning log
   */
  warn(...args: any[]) {
    this.logWithLevel('warn', args);
  }

  /**
   * Error log (always logs unless in test environment)
   */
  error(...args: any[]) {
    if (process.env.NODE_ENV === 'test') return;
    this.logWithLevel('error', args, { force: true });
  }

  /**
   * Debug log with optional module context
   */
  debug(module: string, ...args: any[]) {
    this.logWithLevel('debug', args, { module });
  }

  /**
   * Group related logs together
   */
  group(label: string, fn: () => void, options?: DebugOptions) {
    if (!this.shouldLog(options)) return;

    const groupMethod = options?.collapsed ? console.groupCollapsed : console.group;
    groupMethod(`${this.getPrefix('log', options?.module)} ${label}`);
    fn();
    console.groupEnd();
  }

  /**
   * Time a function execution
   */
  async time<T>(label: string, fn: () => T | Promise<T>, options?: DebugOptions): Promise<T> {
    if (!this.shouldLog(options)) return fn();

    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.logWithLevel('info', [`${label} took ${duration.toFixed(2)}ms`], options);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.logWithLevel('error', [`${label} failed after ${duration.toFixed(2)}ms`, error], options);
      throw error;
    }
  }

  /**
   * Log only once per session (useful for initialization logs)
   */
  private loggedOnce = new Set<string>();

  once(key: string, ...args: any[]) {
    if (this.loggedOnce.has(key)) return;
    this.loggedOnce.add(key);
    this.log(...args);
  }

  /**
   * Create a scoped logger for a specific module
   */
  scope(module: string) {
    return {
      log: (...args: any[]) => this.logWithLevel('log', args, { module }),
      info: (...args: any[]) => this.logWithLevel('info', args, { module }),
      warn: (...args: any[]) => this.logWithLevel('warn', args, { module }),
      error: (...args: any[]) => this.logWithLevel('error', args, { module }),
      debug: (...args: any[]) => this.logWithLevel('debug', args, { module }),
      group: (label: string, fn: () => void, collapsed = false) =>
        this.group(label, fn, { module, collapsed }),
      time: <T>(label: string, fn: () => T | Promise<T>) =>
        this.time(label, fn, { module })
    };
  }
}

// Create singleton instance
const debug = new DebugHelper();

// Export the instance and types
export default debug;
export type { DebugOptions, LogLevel };

// Enable specific modules in development (can be controlled via env vars)
if (process.env.NEXT_PUBLIC_DEBUG_MODULES) {
  const modules = process.env.NEXT_PUBLIC_DEBUG_MODULES.split(',');
  modules.forEach(module => debug.enableModule(module.trim()));
}